import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { formatAddress } from '../lib/blockchain';

function getWalletAddress(props) {
  const wallet = props?.wallet || props;

  return (
    wallet?.account ||
    wallet?.address ||
    wallet?.selectedAccount ||
    props?.account ||
    props?.address ||
    ''
  );
}

function getHistoryItems(history) {
  if (Array.isArray(history)) {
    return history;
  }

  if (Array.isArray(history?.events)) {
    return history.events;
  }

  return [];
}

function getCustodyItems(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.addresses)) {
    return data.addresses;
  }

  if (data && typeof data === 'object') {
    return Object.entries(data).map(([id, value]) => ({
      id,
      ...(value || {}),
    }));
  }

  return [];
}

function pickRelevantCustody(form, chains, custodyAddresses, quote) {
  const matchId = String(form.from_chain || '').toLowerCase();
  const chainMatch =
    chains.find((chain) => String(chain.id || chain.name || '').toLowerCase() === matchId) || null;

  if (quote?.custody_address) {
    return {
      address: quote.custody_address,
      chain: chainMatch?.name || form.from_chain,
      pool_id: quote.pool_id,
    };
  }

  const custodyMatch = custodyAddresses.find((entry) => {
    const candidate = String(entry.id || entry.chain || entry.name || '').toLowerCase();
    return candidate === matchId;
  });

  if (!custodyMatch) {
    return null;
  }

  return {
    address: custodyMatch.address || custodyMatch.custody_address || custodyMatch.wallet || '',
    chain: custodyMatch.chain || custodyMatch.name || form.from_chain,
    pool_id: custodyMatch.pool_id,
  };
}

export default function Bridge(props) {
  const walletAddress = getWalletAddress(props);
  const [chains, setChains] = useState([]);
  const [custodyAddresses, setCustodyAddresses] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyDeposits, setHistoryDeposits] = useState([]);
  const [quote, setQuote] = useState(null);
  const [transferResult, setTransferResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [form, setForm] = useState({
    from_chain: 'Ethereum',
    to_chain: 'iTani',
    asset: 'USDT',
    amount: '',
    from_address: walletAddress,
    destination_address: walletAddress,
    itani_address: walletAddress,
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      from_address: current.from_address || walletAddress,
      destination_address: current.destination_address || walletAddress,
      itani_address: current.itani_address || walletAddress,
    }));
  }, [walletAddress]);

  useEffect(() => {
    let cancelled = false;

    async function loadBridgePage() {
      setLoading(true);
      setError('');

      try {
        const [supportedChains, custodyData, bridgeEvents] = await Promise.all([
          api.wallet.supportedChains(),
          api.bridge.custodyAddresses(),
          api.bridge.events(10),
        ]);

        if (cancelled) {
          return;
        }

        setChains(Array.isArray(supportedChains?.chains) ? supportedChains.chains : []);
        setCustodyAddresses(getCustodyItems(custodyData));
        setRecentEvents(Array.isArray(bridgeEvents?.events) ? bridgeEvents.events : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Unable to load bridge data.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadBridgePage();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!walletAddress) {
      setHistory([]);
      setHistoryDeposits([]);
      setHistoryError('');
      return;
    }

    let cancelled = false;

    async function loadHistory() {
      setHistoryError('');

      try {
        const response = await api.bridge.history(walletAddress, 20);

        if (cancelled) {
          return;
        }

        setHistory(getHistoryItems(response));
        setHistoryDeposits(Array.isArray(response?.deposits) ? response.deposits : []);
      } catch (err) {
        if (!cancelled) {
          setHistoryError(err?.response?.data?.error || err?.message || 'Unable to load bridge history.');
        }
      }
    }

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [walletAddress]);

  const relevantCustody = useMemo(
    () => pickRelevantCustody(form, chains, custodyAddresses, quote),
    [chains, custodyAddresses, form, quote],
  );

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const requestQuote = async () => {
    setQuoting(true);
    setError('');
    setTransferResult(null);

    try {
      const response = await api.bridge.quote({
        from_chain: form.from_chain,
        to_chain: form.to_chain,
        asset: form.asset,
        amount: form.amount,
      });

      setQuote(response);
      return response;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to fetch bridge quote.';
      setError(message);
      throw err;
    } finally {
      setQuoting(false);
    }
  };

  const submitTransfer = async () => {
    setSubmitting(true);
    setError('');

    try {
      const response = await api.bridge.transfer({
        from_chain: form.from_chain,
        to_chain: form.to_chain,
        asset: form.asset,
        amount: form.amount,
        from_address: form.from_address,
        destination_address: form.destination_address,
        itani_address: form.itani_address,
      });

      setTransferResult(response);

      if (walletAddress) {
        const historyResponse = await api.bridge.history(walletAddress, 20);
        setHistory(getHistoryItems(historyResponse));
        setHistoryDeposits(Array.isArray(historyResponse?.deposits) ? historyResponse.deposits : []);
      }

      const bridgeEvents = await api.bridge.events(10);
      setRecentEvents(Array.isArray(bridgeEvents?.events) ? bridgeEvents.events : []);

      return response;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to submit bridge transfer.';
      setError(message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="glass rounded-3xl p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-sm uppercase tracking-[0.3em] text-cyan-300">Bridge</div>
            <h1 className="mt-2 text-3xl font-semibold text-white">Move assets into the iTani flow</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-300">
              Get a route estimate, confirm the custody destination when required, then submit your
              bridge intent for iTani settlement.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Wallet</div>
              <div className="mt-1 text-sm font-medium text-white">
                {walletAddress ? formatAddress(walletAddress) : 'Not connected'}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Supported chains</div>
              <div className="mt-1 text-sm font-medium text-white">{chains.length || 0}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="glass rounded-3xl p-6">
          <div className="mb-5">
            <h2 className="text-xl font-semibold text-white">Quote & transfer</h2>
            <p className="mt-1 text-sm text-slate-300">
              Enter your bridge route. The quote call uses only the route fields, while transfer submits
              the full payload expected by the backend.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm text-slate-300">From chain</span>
              <select
                name="from_chain"
                value={form.from_chain}
                onChange={onChange}
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none"
              >
                {(chains.length ? chains : [{ id: 'Ethereum', name: 'Ethereum' }]).map((chain) => (
                  <option key={chain.id || chain.name} value={chain.name || chain.id}>
                    {chain.name || chain.id}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-sm text-slate-300">To chain</span>
              <select
                name="to_chain"
                value={form.to_chain}
                onChange={onChange}
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none"
              >
                {(chains.length ? chains : [{ id: 'iTani', name: 'iTani' }]).map((chain) => (
                  <option key={chain.id || chain.name} value={chain.name || chain.id}>
                    {chain.name || chain.id}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-sm text-slate-300">Asset</span>
              <input
                name="asset"
                value={form.asset}
                onChange={onChange}
                placeholder="USDT"
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm text-slate-300">Amount</span>
              <input
                name="amount"
                value={form.amount}
                onChange={onChange}
                placeholder="100"
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm text-slate-300">From address</span>
              <input
                name="from_address"
                value={form.from_address}
                onChange={onChange}
                placeholder="Source wallet address"
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm text-slate-300">Destination address</span>
              <input
                name="destination_address"
                value={form.destination_address}
                onChange={onChange}
                placeholder="Destination wallet address"
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500"
              />
            </label>

            <label className="space-y-2 md:col-span-2">
              <span className="text-sm text-slate-300">iTani address</span>
              <input
                name="itani_address"
                value={form.itani_address}
                onChange={onChange}
                placeholder="Recipient on iTani"
                className="w-full rounded-2xl border border-white/10 bg-slate-900 px-4 py-3 text-white outline-none placeholder:text-slate-500"
              />
            </label>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={requestQuote}
              disabled={quoting}
              className="rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {quoting ? 'Loading quote...' : 'Get quote'}
            </button>
            <button
              type="button"
              onClick={submitTransfer}
              disabled={submitting}
              className="rounded-2xl border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting...' : 'Submit transfer'}
            </button>
          </div>

          {error ? (
            <div className="mt-4 rounded-2xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
              {error}
            </div>
          ) : null}
        </section>

        <section className="space-y-6">
          <div className="glass rounded-3xl p-6">
            <h2 className="text-lg font-semibold text-white">Route preview</h2>
            {quote ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Route</div>
                  <div className="mt-1 text-sm font-medium text-white">{quote.route || 'Bridge route'}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Estimated time</div>
                  <div className="mt-1 text-sm font-medium text-white">{quote.estimated_time || 'Pending'}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Fee</div>
                  <div className="mt-1 text-sm font-medium text-white">{quote.fee ?? '-'}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Receive amount</div>
                  <div className="mt-1 text-sm font-medium text-white">{quote.receive_amount ?? '-'}</div>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-400">Request a quote to see fee, route, and timing data.</p>
            )}

            {relevantCustody?.address ? (
              <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-cyan-200">Custody address</div>
                <div className="mt-1 break-all text-sm font-medium text-white">{relevantCustody.address}</div>
                <div className="mt-2 text-xs text-cyan-100">
                  {relevantCustody.chain || form.from_chain}
                  {relevantCustody.pool_id ? ` • Pool ${relevantCustody.pool_id}` : ''}
                </div>
              </div>
            ) : null}

            {transferResult ? (
              <div className="mt-4 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-4">
                <div className="text-xs uppercase tracking-[0.2em] text-emerald-200">Transfer submitted</div>
                <div className="mt-1 text-sm font-medium text-white">
                  {transferResult.bridge_id || transferResult.status || 'Bridge created'}
                </div>
              </div>
            ) : null}
          </div>

          <div className="glass rounded-3xl p-6">
            <h2 className="text-lg font-semibold text-white">Custody registry</h2>
            <div className="mt-4 space-y-3">
              {loading ? (
                <div className="text-sm text-slate-400">Loading custody routes...</div>
              ) : custodyAddresses.length ? (
                custodyAddresses.slice(0, 6).map((entry) => (
                  <div
                    key={entry.id || entry.chain || entry.address}
                    className="rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-medium text-white">{entry.chain || entry.id || 'Chain'}</div>
                      {entry.pool_id ? (
                        <div className="text-xs uppercase tracking-[0.2em] text-cyan-300">{entry.pool_id}</div>
                      ) : null}
                    </div>
                    <div className="mt-2 break-all text-sm text-slate-300">
                      {entry.address || entry.custody_address || entry.wallet || 'Address unavailable'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-400">No custody addresses returned by the backend.</div>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="glass rounded-3xl p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Bridge history</h2>
              <p className="mt-1 text-sm text-slate-400">
                {walletAddress ? `Tracking activity for ${formatAddress(walletAddress)}` : 'Connect a wallet to load bridge history.'}
              </p>
            </div>
          </div>

          {historyError ? (
            <div className="mt-4 rounded-2xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">
              {historyError}
            </div>
          ) : null}

          <div className="mt-4 space-y-3">
            {history.length || historyDeposits.length ? (
              [...history, ...historyDeposits].slice(0, 8).map((item, index) => (
                <div key={`${item.bridge_id || item.tx_hash || item.id || 'item'}-${index}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm font-medium text-white">
                      {item.event_type || item.status || 'Bridge event'}
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                      {item.from_chain || item.chain || 'Chain'} → {item.to_chain || 'iTani'}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-slate-300">
                    {item.amount ? `Amount: ${item.amount}` : 'Amount pending'}
                  </div>
                  {item.tx_hash ? (
                    <div className="mt-2 break-all text-xs text-slate-500">{item.tx_hash}</div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-sm text-slate-400">No bridge history available yet.</div>
            )}
          </div>
        </section>

        <section className="glass rounded-3xl p-6">
          <h2 className="text-lg font-semibold text-white">Recent bridge events</h2>
          <div className="mt-4 space-y-3">
            {recentEvents.length ? (
              recentEvents.map((event, index) => (
                <div key={`${event.id || event.tx_hash || 'bridge'}-${index}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm font-medium text-white">{event.event_type || 'Bridge event'}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                      {event.status || 'observed'}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-slate-300">
                    {event.from_chain || 'Unknown'} → {event.to_chain || 'Unknown'}
                  </div>
                  {event.address ? (
                    <div className="mt-2 text-xs text-slate-500">{formatAddress(event.address)}</div>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="text-sm text-slate-400">No recent bridge events reported.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}